import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Descriptions, Space, Spin, Typography } from 'antd';
import { withDevModalFilePath } from '../common';
import { trpc } from '../../lib/api';
import { CategoryTag } from '../common/CategoryTag';
import { LocationDisplay } from '../common/LocationDisplay';

interface PartDetailModalProps {
  open: boolean;
  partId: string | null;
  onClose: () => void;
}

const { Modal: DevModal } = withDevModalFilePath('client/src/components/parts/PartDetailModal.tsx');
const { Text } = Typography;

export function PartDetailModal({ open, partId, onClose }: PartDetailModalProps) {
  const navigate = useNavigate();

  const enabled = open && Boolean(partId);
  const { data: part, isLoading } = trpc.parts.getById.useQuery(
    { id: partId as string },
    { enabled }
  );

  const title = useMemo(() => {
    if (!partId) return '部品・消耗品';
    return part?.name ? `部品・消耗品: ${part.name}` : '部品・消耗品';
  }, [part?.name, partId]);

  return (
    <DevModal
      open={open}
      title={title}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>閉じる</Button>
          <Button
            type="primary"
            disabled={!partId}
            onClick={() => {
              if (!partId) return;
              navigate(`/app/parts/${partId}`);
              onClose();
            }}
          >
            詳細ページへ
          </Button>
        </Space>
      }
      width={900}
      destroyOnHidden
      centered
    >
      {isLoading ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : part ? (
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="表示ID">{part.displayId ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="型番">{part.partNumber ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="カテゴリ">
            <CategoryTag category={part.category} />
          </Descriptions.Item>
          <Descriptions.Item label="在庫">
            <Text strong>{part.currentStockLevel ?? 0}</Text>
            <Text type="secondary"> / {part.optimalStockLevel ?? 0}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="保管エリア" span={2}>
            {part.location ? (
              <LocationDisplay
                location={
                  part.location?.id
                    ? ({
                        ...part.location,
                        id: part.location.id,
                      } as { id: string; name?: string | null; code?: string | null; icon?: string | null; path?: string | null })
                    : undefined
                }
                locationId={part.location?.id}
                showIcon={true}
                showPath={true}
              />
            ) : (
              <Text type="secondary">未設定</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="メモ" span={2}>
            {part.notes ? <Text>{part.notes}</Text> : <Text type="secondary">-</Text>}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <div style={{ padding: 24 }}>
          <Text type="secondary">データが見つかりませんでした。</Text>
        </div>
      )}
    </DevModal>
  );
}
